import { Flex, Form, InputNumber, Select } from "antd";
import to from "await-to-js";
import dayjs from "dayjs";
import { t } from "i18next";
import React, { useContext, useEffect, useMemo } from "react";

import Search from "antd/es/input/Search";
import { DatePicker } from "../../../../../components/DatePicker/DatePicker";
import { Modal } from "../../../../../components/Modal/Modal";
import { ModalsContext } from "../../../../../context/modals.context";
import { DATE_FORMAT } from "../../../../../core/constants";
import { ministryService } from "../../../../../modules/ministry/ministry.service";
import {
  IMfMarkiceVraceneCreateOrUpdatePayload,
  IMfSveMarkiceVracenihResponse,
} from "../../../../../modules/ministry/ministry.types";
import { useListaKategorijaZaZahtev } from "../../../../../utils/hooks/useListaKategorijaZaZahtev";
import { toastService } from "../../../../../utils/toast.service";
import style from "./AddEditRecordModal.module.scss";
interface IAddEditRecordModalProps {
  refetch: () => void;
}

export const AddEditRecordModal: React.FC<IAddEditRecordModalProps> = ({
  refetch,
}) => {
  const [form] = Form.useForm<IMfMarkiceVraceneCreateOrUpdatePayload>();

  const { isModalVisible, clearModals, setError, modals } =
    useContext(ModalsContext);

  const {
    isLoadingKategorije,
    kategorije,
    isKategorijeDisabled,
    brojZahtevaError,
    handleBrojZahtevaChange,
    resetKategorije,
  } = useListaKategorijaZaZahtev({
    form,
    isMfin: true,
    markicaKategorijeId: null,
  });

  const id = modals.id as number;
  const record = modals.record as IMfSveMarkiceVracenihResponse;

  const onSubmit = async (values: IMfMarkiceVraceneCreateOrUpdatePayload) => {
    const updatedValues = { ...values, id: id };

    const [err, res] = await to(
      ministryService.postMfMarkiceVraceneCreateOrUpdate(updatedValues)
    );
    if (err || !res.success) return setError(res?.errorMessage);

    if (id) {
      refetch();
      clearModals();
      return toastService.success("TOAST.IZMENILI_ZAHTEV");
    }

    toastService.success("TOAST.KREIRAJ_ZAHTEV");
    refetch();
    form.resetFields();
    clearModals();
  };

  const handleClose = () => {
    clearModals();
    form.resetFields();
  };

  const handleOk = () => {
    form.submit();
  };

  const isEditMode = useMemo(() => {
    return !!modals?.id;
  }, [modals]);

  const handleBrojZahtevaSubmit = (brojZahteva: string) => {
    form.setFieldValue("vrstaKategorijeId", null);
    resetKategorije();
    handleBrojZahtevaChange(brojZahteva);
  };

  useEffect(() => {
    form.setFieldsValue({
      kolicina: record ? record?.kolicina : null,
      brojZahteva: record ? record?.brojZahteva : "",
      datumVracanja: record?.datumVracanja
        ? dayjs(record?.datumVracanja)
        : null,
      vrstaKategorijeId: record ? record.vrstaKategorijeId : null,
      datumUnistavanja: record?.datumUnistavanja
        ? dayjs(record?.datumUnistavanja)
        : null,
    });
  }, [record]);

  return (
    <Modal
      title={
        id
          ? t("EVIDENCIJA_MARKICA.IZMENI_ZAHTEV_VRACANJE")
          : t("EVIDENCIJA_MARKICA.NOVI_ZAHTEV_VRACANJE")
      }
      open={isModalVisible("dodajIzmeniEvidencijuModal")}
      onCancel={handleClose}
      onOk={handleOk}
      cancelButtonProps={{ style: { display: "none" } }}
      okText={t("SETTINGS_COMPANY.SACUVAJ")}
      name="add-edit-record-modal"
      destroyOnClose
    >
      <Form
        form={form}
        labelCol={{ span: 8 }}
        onFinish={(values: IMfMarkiceVraceneCreateOrUpdatePayload) =>
          onSubmit(values)
        }
      >
        <Flex vertical justify="space-around" gap={10}>
          <Form.Item
            name="brojZahteva"
            label={t("TABLES.ZAM_ID_ZAHTEVA2")}
            rules={[
              { required: true, message: "" },
              {
                validator: (_, value) => {
                  if (!!value && brojZahtevaError?.brojZahteva === value) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: brojZahtevaError?.message ?? "",
              },
            ]}
            colon={false}
          >
            <Search
              name="brojZahteva"
              placeholder={t("TABLES.ZAM_ID_ZAHTEVA2")}
              enterButton={t("COMMON.POTVRDI")}
              onSearch={(brojZahteva) => handleBrojZahtevaSubmit(brojZahteva)}
              disabled={isEditMode}
              allowClear
            />
          </Form.Item>
          <Form.Item
            name="vrstaKategorijeId"
            colon={false}
            label={t("TABLES.VRSTA_KATEGORIJE")}
            rules={[{ required: true, message: "" }]}
          >
            <Select
              placeholder={t("TABLES.VRSTA_KATEGORIJE")}
              options={kategorije}
              loading={isLoadingKategorije}
              disabled={isEditMode || isKategorijeDisabled}
            />
          </Form.Item>
          <Form.Item
            name="kolicina"
            colon={false}
            label={t("COMMON.KOLICINA")}
            rules={[{ required: true, message: "" }]}
            className={style.formItem}
          >
            <InputNumber
              min={1}
              disabled={isEditMode}
              placeholder={t("COMMON.KOLICINA")}
              name="kolicina"
              className={style.formItem}
            />
          </Form.Item>
          <Form.Item
            name="datumVracanja"
            colon={false}
            label={t("TABLES.DATUM_VRACANJA")}
            rules={[{ required: true, message: "" }]}
          >
            <DatePicker
              showToday={false}
              placeholder={t("TABLES.DATUM_VRACANJA")}
              format={DATE_FORMAT}
              name="datum-vracanja"
            />
          </Form.Item>
          <Form.Item
            name="datumUnistavanja"
            colon={false}
            label={t("TABLES.DATUM_UNISTAVANJA")}
          >
            <DatePicker
              showToday={false}
              placeholder={t("TABLES.DATUM_UNISTAVANJA")}
              format={DATE_FORMAT}
              name="datum-unistavanja"
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
};
